import Vue from 'vue';
import { Module } from 'vuex';
import { RootState } from '@/types/store';
import { isUndef } from '@/lib/utils';

export interface TeamState {
    /** 战队成员 */
    members: {
        [teamId: number]: Team.Members[] | undefined,
    };
    requesting: boolean;
    teamInviteCard: {
        [teamId: number]: string | undefined,
    };
    teamReferrer: {
        [key: string]: string,
    };
    stat: {
        [teamId: number]: Team.Stat | Promise<Team.Stat>;
    };
    history: {
        [teamId: number]: Team.History;
    };
}

const m: Module<TeamState, RootState> = {
    namespaced: true,
    state: () => ({
        members: {},
        moments: {},
        requesting: false,
        teamInviteCard: {},
        teamReferrer: {},
        stat: {},
        history: {},
    }),
    mutations: {
        setMembers(state, { teamId, members }: { teamId: number, members: Team.Members[] }) {
            Vue.set(state.members, teamId, members);
        },
        setRequesting(state, requesting: boolean) {
            state.requesting = requesting;
        },
        setTeamInviteCardMap(state, { teamId, card }: { teamId: number, card: string }) {
            Vue.set(state.teamInviteCard, teamId, card);
        },
        setTeamReferrer(state, { key, data }: { key: string, data: string }) {
            Vue.set(state.teamInviteCard, key, data);
        },
        /** 记录统计数据 */
        setStat(state, { teamId, stat }) {
            Vue.set(state.stat, teamId, stat);
        },
        setHistory(state, { teamId, history }) {
            Vue.set(state.history, teamId, history);
        },
    },
    actions: {
        /**
         * 获取战队成员列表
         *
         * @param {number} teamId
         *
         * @return {Object} rdata
         *  - status 0表示成功，-1表示失败
         *  - message 失败信息
         */
        async getTeamMembers(context, { teamId, forceUpdate }: { teamId: number, forceUpdate?: boolean }) {
            const response = await Vue.axios.get('/api/v1/activity/team_members', {
                params: {
                    id: teamId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setMembers', {
                    teamId,
                    members: rdata.data,
                });
            }
            return response.data;
        },
        /**
         * 获得战队邀请卡
         *
         * @param {string} activityId
         * @param {number} teamId 战队id
         */
        async getTeamInviteCard({ state, commit }, { activityId, teamId, forceUpdate }: { activityId: string, teamId: number, forceUpdate?: boolean }) {
            const temp = state.teamInviteCard[teamId];
            if (temp && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/activity/get_team_invite_card_sync', {
                params: {
                    activity_id: activityId,
                    team_id: teamId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setTeamInviteCardMap', {
                    // activityId,
                    teamId,
                    card: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 记录战队详情页面的访问情况
         *
         * @param {string} referrer
         * @param {number} teamId 战队id
         */
        async recordTeam({ state, commit }, { referrer, teamId, forceUpdate}: { referrer: string, teamId: number, forceUpdate?: boolean }) {
            const key = referrer + '_' + teamId;
            const temp = state.teamReferrer[key];
            if (temp && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.post('/api/v1/activity/team_visit', {
                referrer,
                team_id: teamId,
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setTeamReferrer', {
                    key,
                    data: '',
                });
            }
            return rdata;
        },
        /**
         * 获得统计数据
         */
        async getStat({ state, commit }, { teamId, forceUpdate }) {
            if (state.stat[teamId] && !forceUpdate) {
                return state.stat[teamId];
            }
            const promise = Vue.axios.get('/api/v1/activity/team_statistics', {
                params: {
                    id: teamId,
                },
            });
            commit('setStat', {
                teamId,
                stat: promise,
            });
            const response = await promise;
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setStat', {
                    teamId,
                    stat: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 获得统计数据
         */
        async getHistory({ state, commit }, { teamId, forceUpdate }) {
            if (state.history[teamId] && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/activity/history', {
                params: {
                    id: teamId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setHistory', {
                    teamId,
                    history: rdata.data,
                });
            }
            return rdata;
        },
    },
};

export default m;
